import './on-hover-tooltip-style.less';
import {CrossIcon} from '@esgi/ui-kit/icons';
import React, {ReactNode} from 'react';
import ReactDOM from 'react-dom';
import {Tooltip} from './tooltip';
import {CursorTooltip} from './cursor-tooltip';
import {ClickBehaviorType} from './click-behavior-type';
import styles from './on-hover-tooltip.module.less';

export class OnHoverTooltip extends React.PureComponent<Props, State> {
	public override state = new State();

	private readonly fadeTimeout = 700;
	private mouseLeftRefElement: boolean;
	private hideTooltipTimer: number = null;
	private isUnmounted = false;

	private ref: HTMLElement | SVGElement;

	private get isCursorTooltipMode() {
		return this.props.showUnderCursor || !this.props.placement;
	}

	private get touchDevice() {
		return document.body.parentElement.classList.contains('touch');
	}

	protected get tooltipID() {
		return `tooltip-${this.state.x}${this.state.y}`;
	}

	protected onHoverHandler() {
		if (!this.touchDevice) {
			if (this.isCursorTooltipMode && !this.props.showImmediately && !this.state.shown) {
				setTimeout(() => {
					if (!this.mouseLeftRefElement) {
						this.showTooltip();
					}
				}, this.fadeTimeout);
				this.mouseLeftRefElement = false;
			} else {
				this.showTooltip();
			}
		}
	}

	protected onMouseMove(e: any) {
		if ((!this.state.hovered && !this.state.shown && this.isCursorTooltipMode && !this.touchDevice) || this.props.followForCursor) {
			this.safeSetState({x: e.clientX + 15, y: e.clientY + 15});
		}
	}

	protected onMouseLeaveHandler() {
		if (!this.touchDevice) {
			this.hideTooltip();
		}
	}

	protected onMouseWheelHandler(e: Event) {
		if (!this.touchDevice) {
			this.mouseLeftRefElement = true;
			this.safeSetState({hovered: false});
		}
	}

	protected onClickHandler() {
		if (this.props.clickBehavior === ClickBehaviorType.ImmediatelyHide || this.props.clickBehavior === undefined) {
			this.immediatelyHideTooltip();
			return;
		}
		if (this.props.clickBehavior === ClickBehaviorType.ImmediatelyShow) {
			this.showTooltip();
			return;
		}
	}

	protected onTooltipMouseHoverHandler() {
		this.showTooltip();
	}

	protected onTooltipMouseLeaveHandler() {
		this.hideTooltip();
	}

	public override componentDidMount() {
		if (this.ref) {
			this.ref.addEventListener('mouseenter', () => this.onHoverHandler());
			this.ref.addEventListener('mouseleave', () => this.onMouseLeaveHandler());
			this.ref.addEventListener('wheel', (e) => this.onMouseWheelHandler(e), {passive: true});
			this.ref.addEventListener('mousemove', (e) => this.onMouseMove(e));
			this.ref.addEventListener('click', () => this.onClickHandler());
			this.ref.addEventListener('touchstart', () => this.onClickHandler());

			if (this.touchDevice && this.props.touchBehavior === 'show') {
				this.ref.tabIndex = this.ref.tabIndex > 0 ? this.ref.tabIndex : 0;

				this.ref.addEventListener('focus', () => {
					const rect = this.ref.getBoundingClientRect();
					if(this.props.placement) {
						this.setState({x: rect.x + rect.width, y: rect.y + rect.height}, () => this.showTooltip());
					} else {
						this.setState({x: rect.x + (rect.width/2), y: rect.y + rect.height}, () => this.showTooltip());
					}
				});
				this.ref.addEventListener('blur', () => {
					this.hideTooltip();
				});
				this.ref.addEventListener('touchstart', () => {
					this.ref.focus();
				});
			}
		}
		this.updateRefIfNeed();
	}

	private getChildAsElem() {
		if (!!this.props.children && React.Children.count(this.props.children) === 1) {
			return React.Children.only(this.props.children);
		} else {
			if (this.props.children) {
				throw 'OnHoverTooltip allow only one child node\nYou pass:' + React.Children.count(this.props.children);
			} else {
				throw 'OnHoverTooltip require at least one child';
			}
		}
	}

	public override componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>, snapshot?: any) {
		this.updateRefIfNeed();
	}


	public override componentWillUnmount() {
		this.isUnmounted = true;
		if (this.hideTooltipTimer) {
			clearTimeout(this.hideTooltipTimer);
		}
		this.clearListeners();
	}

	private updateRefIfNeed() {
		// eslint-disable-next-line react/no-find-dom-node
		const element = ReactDOM.findDOMNode(this);
		if(element !== this.ref) {
			this.initRef(element as (HTMLElement | SVGElement));
		}
	}

	private initRef(ref: HTMLElement | SVGElement) {
		this.clearListeners();
		this.ref = ref;
		this.initListeners();
	}

	private initListeners() {
		if (this.ref) {
			this.ref.addEventListener('mouseenter', () => this.onHoverHandler());
			this.ref.addEventListener('mouseleave', () => this.onMouseLeaveHandler());
			this.ref.addEventListener('wheel', (e) => this.onMouseWheelHandler(e), {passive: true});
			this.ref.addEventListener('mousemove', (e) => this.onMouseMove(e));
			this.ref.addEventListener('click', () => this.onClickHandler());
			this.ref.addEventListener('touchstart', () => this.onClickHandler());
		}
	}

	private clearListeners() {
		if (this.ref) {
			this.ref.removeEventListener('mouseenter', () => this.onHoverHandler());
			this.ref.removeEventListener('mouseleave', () => this.onMouseLeaveHandler());
			this.ref.removeEventListener('wheel', (e) => this.onMouseWheelHandler(e));
			this.ref.removeEventListener('mousemove', (e) => this.onMouseMove(e));
			this.ref.removeEventListener('click', () => this.onClickHandler());
			this.ref.removeEventListener('touchstart', () => this.onClickHandler());
		}
	}

	//Check case when the cursor left ref element, but the state wasn't updated to hovered: false
	private checkTooltipHidden(): void {
		if (this.mouseLeftRefElement) {
			const $tooltip = $(`#${this.tooltipID}`);
			if ($tooltip && this.state.hovered) {
				this.safeSetState({hovered: false});
			}
		}
	}

	showTooltip() {
		if (this.hideTooltipTimer) {
			clearTimeout(this.hideTooltipTimer);
			this.hideTooltipTimer = null;
		}
		this.safeSetState({hovered: true, shown: true}, () => this.checkSize());
	}

	hideTooltip() {
		this.mouseLeftRefElement = true;
		this.safeSetState({hovered: false});
		this.hideTooltipTimer = window.setTimeout(() => {
			if (!this.state.hovered) {
				this.safeSetState({shown: false});
				setTimeout(() => this.checkTooltipHidden(), 100);
			}
		}, 70);
	}

	protected immediatelyHideTooltip() {
		this.safeSetState({hovered: false, shown: false});
		setTimeout(() => this.checkTooltipHidden(), 100);
	}

	clearHideTooltipTimer() {
		if (this.hideTooltipTimer) {
			clearTimeout(this.hideTooltipTimer);
			this.hideTooltipTimer = null;
		}
	}

	checkSize() {
		const $tooltip = $(`#${this.tooltipID}`);
		$tooltip.show();
		const tooltipSize = {width: $tooltip.width(), height: $tooltip.height()};
		const screenSize = {width: $(window).width(), height: $(window).height()};
		const newCoords = {x: this.state.x, y: this.state.y};
		let widthChanged = false;
		//Move tooltip to the left if no available space to show it near window border
		if (this.state.x + tooltipSize.width > screenSize.width) {
			newCoords.x = Math.round(newCoords.x - tooltipSize.width);
			widthChanged = true;
			$tooltip.hide();
		}
		//Move tooltip higher if no available space to show it under the cursor
		if (this.state.y + tooltipSize.height > screenSize.height && !widthChanged) {
			newCoords.y = Math.round(newCoords.y - tooltipSize.height);
		}
		this.setState({x: newCoords.x, y: newCoords.y}, () => {
			if (widthChanged) {
				this.checkSize();
			}
		});
	}

	public override render() {
		let tooltip;
		if (this.state.shown && !!this.props.message && this.state.x !== 0 && this.state.y !== 0) {
			tooltip = this.isCursorTooltipMode ? this.renderCursorTooltip() : this.renderDefaultTooltip();
		}

		return <>{this.props.children}{tooltip}</>;
	}

	renderDefaultTooltip() {
		return <Tooltip element={this.ref as HTMLElement} placement={this.props.placement}
						className={`${this.props.className ? this.props.className : 'on-hover-tooltip'}`}>
			{this.props.message}
		</Tooltip>;
	}

	private buildClassNameForCursorTooltip() {
		let out = '';
		if (this.props.className) {
			out += ' ' + this.props.className;
		}
		if (!this.state.hovered) {
			out += ' ' + 'fade-out';
		} else {
			out += ' ' + 'shown';
		}
		return out.trim();
	}

	private renderCursorTooltip() {
		return <CursorTooltip
			id={this.tooltipID}
			x={this.state.x}
			y={this.state.y}
			className={this.buildClassNameForCursorTooltip()}
			onHovered={() => this.onTooltipMouseHoverHandler()}
			onMouseLeave={() => this.onTooltipMouseLeaveHandler()}
		>{this.props.message}
			{this.touchDevice && <div className={styles.closeButton} onTouchStart={() => this.hideTooltip()}>
				<CrossIcon color={'#51C3F2'}/>
			</div>}
		</CursorTooltip>;
	}

	safeSetState(state: Partial<State>, cb?: () => void) {
		if(!this.isUnmounted) {
			this.setState(state as any, () => cb && cb());
		}
	}
}

class Props {
	message: string | JSX.Element;
	showUnderCursor?: boolean;
	followForCursor?: boolean;
	showImmediately?: boolean;
	clickBehavior?: ClickBehaviorType;
	placement?: 'right' | 'top' | 'bottom' | 'left';
	className?: string;
	children?: ReactNode;
	touchBehavior?: 'none' | 'show';
}

class State {
	hovered: boolean = false;
	x: number = 0;
	y: number = 0;
	shown: boolean = false;
	hoveredOverTooltip: boolean = false;
}
